import { Injectable } from '@angular/core';
import { ContentOutletComponent } from '@agingplan';
import { firstValueFrom, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  /**
   * Is the Navbar currently opaque with the close button visible?
   */
  public opaque$ = new Subject<boolean>();

  /**
   * Is the full screen loader visible?
   */
  public fullScreenLoaderVisible$ = new Subject<boolean>();

  /**
   * Is the gallery loader visible?
   */
  public galleryLoaderVisible$ = new Subject<boolean>();

  /**
   * A counter of the number of items in the work queue. The full screen loader will be visible if this is greater than 0
   */
  private work_queue = 0;

  /** Navbar Theme */
  public navbar_theme: 'navbar-dark' | 'navbar-light' = 'navbar-light';

  /** Navbar Logo */
  public logo = NavbarLogo.LIGHT;

  constructor() {
    // this.fullScreenLoaderVisible$.subscribe(console.log);
    this.fullScreenLoaderVisible$.next(true);
    this.galleryLoaderVisible$.next(true);
  }

  /**
   * Add an item to the work queue. This should be called when an async operation is started
   */
  public addToWorkQueue(): void {
    this.work_queue++;
    this.fullScreenLoaderVisible$.next(this.work_queue > 0);
  }

  /**
   * Remove an item from the work queue. This should be called when an async operation is completed
   */
  public removeFromWorkQueue(component?: string): void {
    this.work_queue--;
    this.fullScreenLoaderVisible$.next(this.work_queue > 0);
  }

  public toggleGalleryLoader(visible: boolean): void {
    this.galleryLoaderVisible$.next(visible);
  }

  /**
   * Destroy the navbar, remove it completely from the DOM
   */
  public destroyTopNavigation(): void {
    document.getElementById('navbar')?.remove();
  }

  /**
   * Destroy the footer, remove it completely from the DOM
   */
  public destroyFooter(): void {
    document.getElementById('footer')?.remove();
  }

  /**
   * Destroy the footer, remove it completely from the DOM
   */
  public hideFooter(): void {
    const footer = document.getElementById('footer');
    if (footer) footer.style.display = 'none';
  }

  /**
   * Destroy the footer, remove it completely from the DOM
   */
  public showFooter(): void {
    const footer = document.getElementById('footer');
    if (footer) footer.style.display = 'block';
  }

  /**
   * Destroy the Top navbar, remove it completely from the DOM
   */
  public hideNavbar(): void {
    const footer = document.getElementById('navbar');
    if (footer) footer.style.display = 'none';
  }

  /**
   * Show Top navbar
   */
  public showNavbar(): void {
    const footer = document.getElementById('navbar');
    if (footer) footer.style.display = 'block';
  }

  /**
   * Open a content outlet for a component
   * @param {ContentOutletComponent} popover - The content outlet component
   */
  public openPopover(popover: ContentOutletComponent) {
    firstValueFrom(popover.onOpened).then(() => {
      this.opaque$.next(true);
    });

    firstValueFrom(popover.onClosed).then(() => {
      this.opaque$.next(false);
    });

    popover.open();
  }

  /** Change Navbar Theme
   * @param theme - LIGHT | DARK
   */
  public changeNavbarTheme(theme: 'light' | 'dark') {
    if (theme === 'dark') {
      this.navbar_theme = 'navbar-dark';
      this.logo = NavbarLogo.DARK;
    } else {
      this.navbar_theme = 'navbar-light';
      this.logo = NavbarLogo.LIGHT;
    }
  }

}

/**
 * Navbar Logo.
 */
export enum NavbarLogo {
  LIGHT = '/assets/agingplan/logo/AgingPlan_LightBackground.svg',
  DARK = '/assets/agingplan/logo/AgingPlan_DarkBackground.svg'
}
